// src/components/NotificationBar.tsx
import React, { useEffect, useState, useCallback, CSSProperties } from 'react';

// 必要に応じてテーマから色を利用する場合、下記のようにテーマオブジェクトを import してください。
// import { theme } from "../styles/theme";

interface NotificationBarProps {
  /** 通知のメッセージ */
  message: string;
  /** 背景色: theme key or custom color code */
  bgColor?: string;
  /** 文字色: theme key or custom color code */
  textColor?: string;
  /** 通知を表示するかどうか */
  isOpen: boolean;
  /** 通知を閉じるイベント */
  onClose: () => void;
  /** 通知が表示されてから自動的に閉じるまでの時間(ミリ秒) */
  duration?: number;
}

const NotificationBar: React.FC<NotificationBarProps> = ({
  message,
  bgColor,
  textColor,
  isOpen,
  onClose,
  duration = 10000,
}) => {
  const [visible, setVisible] = useState(isOpen);

  // 親から isOpen が変化したら状態を更新
  useEffect(() => {
    setVisible(isOpen);
  }, [isOpen]);

  // onClose を呼ぶ関数
  const handleClose = useCallback(() => {
    setVisible(false);
    onClose();
  }, [onClose]);

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (visible) {
      timer = setTimeout(handleClose, duration);
    }
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [visible, duration, handleClose]);

  // テーマを利用する場合は、下記の computedBgColor や computedTextColor の部分を
  // テーマから取得する処理に書き換えてください。
  const computedBgColor = bgColor || '#333';
  const computedTextColor = textColor || '#fff';

  const barContainerStyle: CSSProperties = {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100%',
    backgroundColor: computedBgColor,
    color: computedTextColor,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '1rem',
    transition: 'transform 0.3s ease-in-out',
    transform: visible ? 'translateY(0%)' : 'translateY(-100%)',
    zIndex: 9999,
  };

  const messageStyle: CSSProperties = {
    fontSize: '1rem',
  };

  const closeButtonStyle: CSSProperties = {
    background: 'transparent',
    border: 'none',
    color: 'inherit',
    fontSize: '1.8rem',
    cursor: 'pointer',
    marginRight: '2rem',
    // ※ inline style では hover 擬似クラスが使えないため、必要に応じ onMouseEnter/onMouseLeave で状態管理する方法も検討してください
  };

  return (
    <div style={barContainerStyle}>
      <span style={messageStyle}>{message}</span>
      <button style={closeButtonStyle} onClick={handleClose}>
        ×
      </button>
    </div>
  );
};

export default NotificationBar;
